import { useEffect, useState } from "react";
import InputWithLabel from "../../../components/shared/fields/InputWithLabel";
import {
  useCreateRejectedSortingDataMutation,
  useCreateSortingDataMutation,
} from "./sortingApi";
import { Col, Row } from "react-bootstrap";
import Button from "../../../components/shared/buttons/button";
import CommonModal from "../../../components/shared/modalLayout/CommonModal";
import { useGetFruitsQuery } from "../../Receiving/unloading/unloadingApi";
import SelectBox from "../../../components/shared/fields/SelectBox ";
import InputWithIcon from "../../../components/shared/fields/InputWithIcon";
import { RefreshSvg } from "../../../svgFiles/RefreshSvg";
import useTranslator from "../../../hooks/useTranslator.js";
import toast from "react-hot-toast";
import {
  EMPTY_ORANGE_CASE_WEIGHT,
  PALLET_ORANGE_CASES,
  PLASTIC_EMPTY_PALLET_WEIGHT,
  WASHING_PALLETIZE_PALLET_MAX_WEIGHT,
  WASHING_PALLETIZE_PALLET_MIN_WEIGHT,
  SORTING_PALLET_MAX_WEIGHT
} from "../../../helpers/constants.js";
import { ceilToTwoDecimal, convertToUserTimezone, getCurrentDateTime } from "../../../helpers/Utils.jsx";

// Define log time options array with value and label
const LOG_TIME_OPTIONS = [
  { value: "morning", label: "Morning" },
  { value: "evening", label: "Evening" },
];

let interval = null;
const SortingModal = ({ show, setShow, record, mode }) => {
  // console.log(`Show : ${show}  setShow : ${setShow}  | mode  : ${mode}`)
  const { _t } = useTranslator();
  const [createRecord, { isLoading: isCreating }] =
    useCreateSortingDataMutation();
  const [createRejectedRecord, { isLoading: isCreatingRejected }] =
    useCreateRejectedSortingDataMutation();
  const [modalTitle,setModalTitle] = useState(_t("adding_sorting_information", "Adding Sorting Information"));  

  const initialFormData = {
    pallet_no: "",
    entered_date_time: convertToUserTimezone(null,"YYYY-MM-DDTHH:mm"),
    weight: "",
    fruit_type_id: "",
    case_count: "36",
    // log_time: LOG_TIME_OPTIONS[0].value, // Default to "morning"
    log_time: "", // Default to "morning"
    total_pallet_weight:0
  };

  const [formData, setFormData] = useState(initialFormData);
  const { data: fruitList } = useGetFruitsQuery();

  const [palletWeight, setPalletWeight] = useState("");

  useEffect(() => {
    if(mode == 'rejected'){
      setModalTitle(_t("adding_rejecting_information", "Adding Rejected Information"));
    }else{
      setModalTitle(_t("adding_sorting_information", "Adding Sorting Information"));
    }
    setFormData(initialFormData);
  }, [mode, record, show]);

  useEffect(() => {
    clearTimeout(interval);
    interval = setTimeout(() => {
      let fruitWeight = 0;
      const caseCount = formData.case_count || "36";

      const isValidInput =
        palletWeight !== "" &&
        caseCount !== "" &&
        +palletWeight !== 0 &&
        +caseCount !== 0;

      if (isValidInput) {
        fruitWeight =
          palletWeight -
          (PLASTIC_EMPTY_PALLET_WEIGHT + EMPTY_ORANGE_CASE_WEIGHT * caseCount);

        if (fruitWeight < 0) {
          fruitWeight = 0;
        }
        fruitWeight = ceilToTwoDecimal(fruitWeight);
      }

      setFormData((prev) => ({
        ...prev,
        weight: fruitWeight,
        total_pallet_weight:palletWeight
      }));
    }, 200);
  }, [palletWeight, formData?.case_count]);

  const handleSubmit = async (e) => {
    // prevent double submission
    if (isCreating || isCreatingRejected) return;

    const palletKg = parseFloat(formData?.weight);
    const total_pallet_weight  = formData?.total_pallet_weight;

    if (isNaN(palletKg)) {
      toast.error(
        _t("please_enter_a_valid_pallet_kg", "Please enter a valid pallet weight (in kg)")
      );
      return;
    }
    
    if (palletKg <= 0) {
      toast.error(
        _t("please_enter_a_valid_pallet_kg", "Please enter a valid pallet weight (in kg)")
      );
      return;
    }

    if (palletKg > SORTING_PALLET_MAX_WEIGHT) {
      // toast.error(
      //   `Pallet weight cannot exceed ${SORTING_PALLET_MAX_WEIGHT} kg.`
      // );

      toast.error(_t(
        "pallet_weight_exceed",
        "Pallet weight cannot exceed :limit kg.",
        { limit: SORTING_PALLET_MAX_WEIGHT }
      ));
      return;
    }

    if (formData?.case_count <= 0) {
      toast.error(_t("invalid_case_count", "Please enter a valid number of cases."));
      return;
    }

    if(formData?.case_count == 36 &&  total_pallet_weight < 1000){
      toast.error(_t("minimum_value_of_36_cases_error", "For 36-case pallets, the minimum weight must be 1000 kg."));
      return;
    }

    // Validate log_time for rejected mode
    if (mode === "rejected") {
      const validLogTimes = LOG_TIME_OPTIONS.map(option => option.value);
      if (!formData?.log_time || !validLogTimes.includes(formData.log_time)) {
        toast.error(_t("please_select_valid_log_time", "Please select a shift time (Morning or Evening)."));
        return;
      }
    }    
    const mutation = mode === "add" ? createRecord : createRejectedRecord;
    try {
      const payload = {
        ...formData,
      };
      await mutation(payload).unwrap();
      handleClose();
    } catch (error) {
      console.error("Error creating/updating sorting record:", error);
    }
  };

  const handleClose = () => {
    setShow(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };



  const renderFormFields = () => {
    return (
      <>
        {renderCommonFields()}
        
        {mode === "rejected" && (
          <Col lg={6}>
            <SelectBox
              label={_t("log_time", "Log Time")}
              name="log_time"
              value={formData.log_time}
              options={LOG_TIME_OPTIONS.map((option) => ({
                value: option.value,
                label: _t(option.value, option.label),
              }))}
              onChange={handleChange}
              required={false}
            />
          </Col>
        )}
      </>
    );
  };

  const renderCommonFields = () => {
    return (
      <>
        <Col lg={6}>
          <InputWithLabel
            label={_t("date_time", "Date/Time")}
            type="datetime-local"
            name="entered_date_time"
            value={formData.entered_date_time}
            onChange={handleChange}
            max={getCurrentDateTime("YYYY-MM-DDTHH:mm")}      
            disabled={true}
          />
        </Col>
        <Col lg={6}>
          <SelectBox
            label={_t("case_count", "Case Count")}
            placeholder={_t("case_count_placeholder", "Case Count...")}
            name="case_count"
            value={formData.case_count}
            options={Array.from({ length: 36 }, (_, i) => ({
              value: (36 - i).toString(),
              label: (36 - i).toString(),
            }))}
            onChange={handleChange}
          />
        </Col>
        <Col lg={6}>
          <InputWithIcon
            label={_t("pallet_weight", "Pallet Weight")}
            placeholder={"0"}
            type="number"
            name="weight"
            value={palletWeight}
            onChange={(e) => setPalletWeight(e.target.value)}
            icon={RefreshSvg}
          />
        </Col>
        <Col lg={6}>
          <InputWithIcon
            label={_t("weight", "Weight")}
            placeholder={"0"}
            type="number"
            name="weight"
            value={formData.weight}
            disabled
          />
        </Col>
        <Col lg={6}>
          <SelectBox
            label={_t("fruit_type", "Fruit Type")}
            name="fruit_type_id"
            options={fruitList?.data.map((list) => ({
              value: list.id,
              label: list.name,
            }))}
            value={formData.fruit_type_id}
            required={false}
            onChange={handleChange}
          />
        </Col>
      </>
    );
  };
  // console.log(`Sorting Adding :: `);

  return (
    <CommonModal
      show={show}
      handleClose={handleClose}
      setShow={setShow}
      title={modalTitle}
      children={formData}
    >
      
      <Row>{renderFormFields()}</Row>
      <Row>
        <Col className="text-end">
          <Button
            label={_t("save_changes", "Save Changes")}
            size="small"
            onClick={handleSubmit}
            disabled={isCreating || isCreatingRejected}
          />
        </Col>
      </Row>
    </CommonModal>
  );
};

export default SortingModal;
